import React, { useEffect, useState } from 'react';
import { TextArea } from '/imports/ui/TextArea';

/**
 * A textarea that edits JSON and stores the parsed object.
 *
 * Parses on every keystroke and only writes to the form state when the text is
 * both valid JSON and passes `validate` -- so a half-typed value can never be
 * saved, while the raw text stays exactly as typed (reformatting mid-edit would
 * fight the cursor).
 */
export const JsonField = <T,>({
  initial, field, label, tip, validate, emptyValue, setUpdate, className,
}: {
  initial?: unknown;
  field: keyof T;
  label: string;
  tip?: string;
  /** Return an error string, or null when acceptable. */
  validate: (value: unknown) => string | null;
  /**
   * Stored when the box is cleared. USE NULL TO UNSET, not undefined: method
   * args are EJSON-serialised and undefined keys are dropped in transit, so an
   * undefined emptyValue makes clearing a silent no-op -- the old value stays.
   */
  emptyValue: unknown;
  setUpdate: (value: React.SetStateAction<Partial<T>>) => void;
  className?: string;
}) => {
  const seed = initial !== undefined ? JSON.stringify(initial, null, 2) : '';
  const [text, setText] = useState(seed);
  const [error, setError] = useState<string | null>(null);
  // Re-seed when the document finishes loading (initial arrives async).
  useEffect(() => { setText(seed); }, [seed]);

  const onChange = (t: string) => {
    setText(t);
    if (!t.trim()) {
      setError(null);
      setUpdate((prev) => ({ ...prev, [field]: emptyValue }));
      return;
    }
    let parsed: unknown;
    try { parsed = JSON.parse(t); }
    catch { setError('Invalid JSON'); return; }
    const err = validate(parsed);
    setError(err);
    if (!err) setUpdate((prev) => ({ ...prev, [field]: parsed }));
  };

  return (
    <div className="json-field">
      <label>{label}</label>
      {tip && <div className="mb-1 text-caption text-fg-3">{tip}</div>}
      <TextArea className={`w-full ${className ?? ''}`} value={text} onChange={onChange} />
      {error && <div className="field-error">{error}</div>}
    </div>
  );
};

/**
 * Validator for a Mongo query field.
 *
 * Shape only: it must be a plain object, because that is what reaches
 * `db.query('cards', ...)`. Operator correctness is NOT checked here -- a query
 * can be perfectly well-formed and still match nothing, which is why the forms
 * that use this also show a live match count.
 */
export const validateMongoQuery = (value: unknown): string | null => {
  if (value === null || typeof value !== 'object') return 'Must be a JSON object, e.g. {"setName": "HOB"}';
  if (Array.isArray(value)) return 'Must be a JSON object, not an array';
  return null;
};
